import React, { useMemo, useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPendingApprovalsRequest } from '../../redux/actions/approvalActions';
import {
  FileTextOutlined,
  AppstoreOutlined,
  TeamOutlined,
  AuditOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  UserOutlined,
  SafetyOutlined,
  InboxOutlined,
  FileDoneOutlined,
  BarChartOutlined,
  DollarOutlined,
  TrophyOutlined,
  CheckSquareOutlined,
  RiseOutlined,
  ReconciliationOutlined,
  RollbackOutlined,
  SyncOutlined,
  FundOutlined,
} from '@ant-design/icons';
import { Badge } from 'antd';
import { Tooltip } from 'antd';
import { hasPermission } from '../../utils/permissions';
import './Sidebar.css';

// Menu items configuration with permission requirements and proper icons
const menuItems = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: HomeOutlined,
    permission: null,
    description: 'Overview and statistics'
  },
  {
    path: '/quotations',
    label: 'Quotations',
    icon: ReconciliationOutlined,
    permission: 'purchase_order/view',
    description: 'Vendor quotes & comparative statement'
  },
  {
    path: '/purchase-order',
    label: 'Purchase Orders',
    icon: FileTextOutlined,
    permission: 'purchase_order/view',
    description: 'Manage purchase orders'
  },
  {
    path: '/delivery-challans',
    label: 'Delivery Challans',
    icon: InboxOutlined,
    permission: 'delivery_challan/view',
    description: 'Manage delivery challans'
  },
  {
    path: '/bill-receipts',
    label: 'Bill Receipts',
    icon: FileDoneOutlined,
    permission: 'bill_receipt/view',
    description: 'Track and manage vendor bills'
  },
  {
    path: '/returns',
    label: 'Returns',
    icon: RollbackOutlined,
    permission: 'delivery_challan/view',
    description: 'Return orders & credit notes'
  },
  {
    path: '/payments',
    label: 'Payments',
    icon: DollarOutlined,
    permission: 'bill_receipt/view',
    description: 'Vendor payment tracking'
  },
  {
    path: '/approvals',
    label: 'Approvals',
    icon: CheckSquareOutlined,
    permission: 'purchase_order/update',
    description: 'PO approval inbox',
    badgeKey: 'pendingApprovals',
  },
  {
    path: '/analytics',
    label: 'Analytics',
    icon: BarChartOutlined,
    permission: 'purchase_order/view',
    description: 'Purchase analytics and insights'
  },
  {
    path: '/analytics/month-comparison',
    label: 'Spend Comparison',
    icon: FundOutlined,
    permission: 'purchase_order/view',
    description: 'Month-on-month spend comparison'
  },
  {
    path: '/price-inflation',
    label: 'Price Inflation',
    icon: RiseOutlined,
    permission: 'purchase_order/view',
    description: 'Product price change report'
  },
  {
    path: '/products',
    label: 'Products',
    icon: AppstoreOutlined,
    permission: 'product/view',
    description: 'Product catalog'
  },
  {
    path: '/vendors',
    label: 'Vendors',
    icon: TeamOutlined,
    permission: 'vendor/view',
    description: 'Vendor management'
  },
  {
    path: '/vendor-performance',
    label: 'Vendor Scores',
    icon: TrophyOutlined,
    permission: 'purchase_order/view',
    description: 'Vendor performance leaderboard'
  },
  {
    path: '/recurring-pos',
    label: 'Recurring POs',
    icon: SyncOutlined,
    permission: null,
    adminOnly: true,
    description: 'Automated recurring purchase orders'
  },
  {
    path: '/rbac',
    label: 'RBAC Management',
    icon: SafetyOutlined,
    permission: 'role/view',
    description: 'Role-based access control'
  },
  {
    path: '/audit-logs',
    label: 'Audit Logs',
    icon: AuditOutlined,
    permission: 'audit/view',
    description: 'System activity logs'
  },
  {
    path: '/site-configurations',
    label: 'Configurations',
    icon: SettingOutlined,
    permission: 'config/view',
    description: 'System settings'
  }
];

function Sidebar() {
  const dispatch = useDispatch();
  const { isAuthenticated, permissions, user } = useSelector(state => state.auth);
  const pendingApprovals = useSelector(state => state.approvals?.pendingApprovals || []);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  const canApprove = hasPermission(permissions || [], 'purchase_order/update');

  useEffect(() => {
    if (isAuthenticated && canApprove) {
      dispatch(fetchPendingApprovalsRequest());
    }
  }, [isAuthenticated, canApprove, dispatch]);

  // Filter menu items based on permissions from Redux
  const visibleMenuItems = useMemo(() => {
    return menuItems.filter(item => {
      if (item.adminOnly) return user?.role === 'admin';
      if (!item.permission) return true;
      return hasPermission(permissions || [], item.permission);
    });
  }, [permissions, user]);

  if (!isAuthenticated) return null;

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <div className="brand-icon">
            <HomeOutlined />
          </div>
          {!collapsed && (
            <div className="brand-text">
              <h2>Kalapi Prints</h2>
              <p>Purchase Management</p>
            </div>
          )}
        </div>
        <button
          className="sidebar-toggle"
          onClick={toggleSidebar}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="sidebar-nav">
        <div className="nav-section">
          {!collapsed && <div className="nav-section-title">Main Menu</div>}
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const badgeCount = item.badgeKey === 'pendingApprovals' ? pendingApprovals.length : 0;
            // Only exact-match this link if another menu item's path is nested under it
            // (e.g. "/analytics" vs "/analytics/month-comparison"), so both don't light up together.
            const hasNestedSibling = visibleMenuItems.some(
              (other) => other.path !== item.path && other.path.startsWith(`${item.path}/`)
            );

            const linkContent = (
              <NavLink
                key={item.path}
                to={item.path}
                end={hasNestedSibling}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
              >
                <div className="link-content">
                  <span className="sidebar-icon">
                    <Badge count={collapsed ? badgeCount : 0} size="small" offset={[4, -4]}>
                      <Icon />
                    </Badge>
                  </span>
                  {!collapsed && (
                    <>
                      <span className="sidebar-label">
                        {item.label}
                        {badgeCount > 0 && (
                          <Badge count={badgeCount} size="small" style={{ marginLeft: 6 }} />
                        )}
                      </span>
                      {isActive && <span className="active-indicator" />}
                    </>
                  )}
                </div>
              </NavLink>
            );

            // Show tooltip when collapsed
            return collapsed ? (
              <Tooltip
                key={item.path}
                title={item.label}
                placement="right"
                mouseEnterDelay={0.3}
              >
                {linkContent}
              </Tooltip>
            ) : (
              linkContent
            );
          })}
        </div>
      </nav>

      {/* Sidebar Footer */}
      {!collapsed && (
        <div className="sidebar-footer">
          <div className="user-badge">
            <div className="user-avatar">
              <UserOutlined />
            </div>
            <div className="user-details">
              <div className="user-name">{user?.username || 'User'}</div>
              <div className="user-role">{user?.role || 'Member'}</div>
            </div>
          </div>
          <div className="app-version">v1.0.0</div>
        </div>
      )}
    </div>
  );
}

export default Sidebar;